import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import {
  PageHeader, ListToolbar, Card, Badge, HealthBadge, Avatar, EmptyState,
  Modal, Button, Field, TextInput, Select, FormGrid,
  DataTable, useViewMode, type Column,
} from '@/components/ui'
import { Sparkline } from '@/components/charts'
import { C } from '@/lib/colors'
import { useStore } from '@/store/useStore'
import {
  propertiesForAccount, openItemsForAccount, latest, fundsForAccount,
} from '@/lib/selectors'
import { money, num, pct } from '@/lib/format'
import type { Account, Property, Dataset, PropertyHealth } from '@/types/entities'
import type { BadgeTone } from '@/components/ui'
import './AccountsList.css'

type ViewId = 'all' | 'investors' | '3pm' | 'prospects' | 'individuals' | 'internal' | 'archived'

// 'All' = the relationship book (institutional investors, 3PM, prospects).
// Small external title-holders (type Personal — individuals, trusts, 1-2
// asset LLCs; tagged by the API's dataset assembler) live under Individuals,
// and our own SPEs are folded into the single Internal account.
const VIEWS = [
  { id: 'all', label: 'All' },
  { id: 'investors', label: 'Investors' },
  { id: '3pm', label: '3PM' },
  { id: 'prospects', label: 'Prospects' },
  { id: 'individuals', label: 'Individuals' },
  { id: 'internal', label: 'Internal' },
  { id: 'archived', label: 'Archived' },
]

const TYPE_TONE: Record<string, BadgeTone> = {
  Investor: 'navy', JV: 'navy', '3PM': 'info', Vendor: 'neutral',
  Broker: 'neutral', 'Municipal/Government': 'neutral', Personal: 'neutral',
}

const isProspect = (a: Account) => a.status === 'Prospect' || a.status === 'Suspect'

interface AccountCardData {
  account: Account
  type: string
  status: string
  propertyCount: number
  owned: number
  managed: number
  health?: PropertyHealth
  occupancy?: number
  occSeries: number[]
  inPlaceRevenue?: number
  openItems: number
  fundCount: number
  prospect: boolean
}

function buildCard(data: Dataset, account: Account): AccountCardData {
  const props = propertiesForAccount(data, account.id)
  const owned = props.filter((p) => p.relationshipType === 'Owned').length
  const managed = props.filter((p) => p.relationshipType === 'Managed-3PM').length

  // Portfolio occupancy: weight latest snapshot per property by NRSF.
  let occWeighted = 0
  let occWeight = 0
  let inPlaceRevenue = 0
  for (const p of props) {
    const occ = latest(p.occupancyPct)
    const w = p.nrsf ?? 0
    if (occ != null && w > 0) { occWeighted += occ * w; occWeight += w }
    const rev = latest(p.revenue)
    if (rev != null) inPlaceRevenue += rev
  }
  const occupancy = occWeight > 0 ? occWeighted / occWeight : undefined

  // Portfolio occupancy series across the 7 aligned snapshots (NRSF-weighted per index).
  const occSeries = portfolioOccSeries(props)

  return {
    account,
    type: account.type,
    status: account.status,
    propertyCount: props.length || account.metrics?.propertyCount || 0,
    owned: owned || account.metrics?.ownedCount || 0,
    managed: managed || account.metrics?.managedCount || 0,
    health: account.metrics?.health,
    occupancy,
    occSeries,
    inPlaceRevenue: inPlaceRevenue > 0 ? inPlaceRevenue : undefined,
    openItems: openItemsForAccount(data, account.id).length || account.metrics?.openItems || 0,
    fundCount: fundsForAccount(data, account.id).length,
    prospect: isProspect(account),
  }
}

/** NRSF-weighted portfolio occupancy by snapshot index (snapshots are aligned 7-wide in seed). */
function portfolioOccSeries(props: Property[]): number[] {
  const maxLen = props.reduce((m, p) => Math.max(m, p.occupancyPct?.length ?? 0), 0)
  if (maxLen < 2) return []
  const out: number[] = []
  for (let i = 0; i < maxLen; i++) {
    let wSum = 0
    let vSum = 0
    for (const p of props) {
      const snaps = p.occupancyPct
      if (!snaps || snaps.length !== maxLen) continue
      const w = p.nrsf ?? 1
      vSum += snaps[i].value * w
      wSum += w
    }
    if (wSum > 0) out.push(vSum / wSum)
  }
  return out
}

export default function AccountsList() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [view, setView] = useState<ViewId>('all')
  const [search, setSearch] = useState('')
  const [addOpen, setAddOpen] = useState(false)
  const [viewMode, setViewMode] = useViewMode('accounts', 'cards')

  const cards = useMemo(() => {
    return data.accounts
      .filter((a) => a.id !== 'acct-wpi' || view === 'internal')
      .map((a) => buildCard(data, a))
  }, [data, view])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return cards.filter((c) => {
      const isInvestor = c.type === 'Investor' || c.type === 'JV'
      if (view === 'archived' && !c.account.archived) return false
      if (view !== 'archived' && c.account.archived) return false
      if (view === 'investors' && (!isInvestor || c.prospect)) return false
      if (view === '3pm' && c.type !== '3PM') return false
      if (view === 'prospects' && !c.prospect) return false
      if (view === 'individuals' && (c.type !== 'Personal' || c.account.id === 'acct-wpi')) return false
      if (view === 'internal' && c.account.id !== 'acct-wpi') return false
      if (view === 'all' && (c.account.id === 'acct-wpi' || c.type === 'Personal')) return false
      if (q) {
        const hay = `${c.account.name} ${c.type} ${c.status} ${c.account.logoText ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    }).sort((a, b) => b.propertyCount - a.propertyCount)
  }, [cards, view, search])

  const cols: Column<AccountCardData>[] = [
    {
      key: 'account',
      header: 'Account',
      sortValue: (c) => c.account.name.toLowerCase(),
      render: (c) => (
        <div className="acct-row-id">
          <Avatar name={c.account.logoText ?? c.account.name} size={32} tone={c.prospect ? 'slate' : 'navy'} />
          <div className="acct-row-id-text">
            <Link to={`/accounts/${c.account.id}`} className="acct-row-name" onClick={(e) => e.stopPropagation()}>
              {c.account.name}
            </Link>
            {c.prospect && c.account.sourceReferredBy && (
              <span className="acct-row-sub dim">Sourced by {c.account.sourceReferredBy}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortValue: (c) => c.type,
      render: (c) => <Badge tone={TYPE_TONE[c.type] ?? 'neutral'} variant="outline">{c.type}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (c) => c.status,
      render: (c) => <span className="muted">{c.status}</span>,
    },
    {
      key: 'health',
      header: 'Health',
      sortValue: (c) => c.health ?? 'zzz',
      render: (c) => (c.prospect ? <span className="dim">—</span> : <HealthBadge health={c.health} />),
    },
    {
      key: 'props',
      header: 'Properties',
      align: 'right',
      sortValue: (c) => c.propertyCount,
      render: (c) => {
        if (c.prospect || c.propertyCount === 0) return <span className="dim">—</span>
        const split = [c.owned ? `${num(c.owned)} owned` : '', c.managed ? `${num(c.managed)} managed` : '']
          .filter(Boolean).join(' · ')
        return (
          <span className="acct-row-props">
            <span className="num">{num(c.propertyCount)}</span>
            {split && <span className="acct-row-sub dim">{split}</span>}
          </span>
        )
      },
    },
    {
      key: 'occ',
      header: 'Portfolio Occ.',
      align: 'right',
      sortValue: (c) => c.occupancy ?? -1,
      render: (c) => (c.occupancy != null ? <span className="num">{pct(c.occupancy)}</span> : <span className="dim">—</span>),
    },
    {
      key: 'rev',
      header: 'In-Place Rev / mo',
      align: 'right',
      sortValue: (c) => c.inPlaceRevenue ?? -1,
      render: (c) => (c.inPlaceRevenue != null
        ? <span className="num">{money(c.inPlaceRevenue, { compact: true })}</span>
        : <span className="dim">—</span>),
    },
    {
      key: 'open',
      header: 'Open items',
      align: 'right',
      sortValue: (c) => c.openItems,
      render: (c) => (c.openItems > 0
        ? <span className="num acct-row-open">{num(c.openItems)}</span>
        : <span className="dim">—</span>),
    },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Relationships"
        title={<>All <b>Accounts</b></>}
        sub="Investors, third-party-management clients, and prospect capital across the WPI portfolio."
      />

      <ListToolbar
        views={VIEWS}
        activeView={view}
        onView={(id) => setView(id as ViewId)}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Search accounts…"
        count={filtered.length}
        onAdd={() => setAddOpen(true)}
        addLabel="New account"
        viewMode={viewMode}
        onViewMode={setViewMode}
      />

      {filtered.length === 0 ? (
        <Card className="acct-empty-wrap">
          <EmptyState
            icon={<Building2 size={36} />}
            title="No accounts in this view"
            hint="Try a different view or clear the search."
          />
        </Card>
      ) : viewMode === 'cards' ? (
        <div className="acct-grid">
          {filtered.map((c) => (
            <AccountCard key={c.account.id} card={c} onOpen={() => navigate(`/accounts/${c.account.id}`)} />
          ))}
        </div>
      ) : (
        <Card className="acct-table-card">
          <DataTable<AccountCardData>
            columns={cols}
            rows={filtered}
            getRowKey={(c) => c.account.id}
            onRowClick={(c) => navigate(`/accounts/${c.account.id}`)}
            initialSort={{ key: 'props', dir: 'desc' }}
            emptyText="No accounts in this view."
          />
        </Card>
      )}

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title={<>New <b>account</b></>}
        footer={
          <>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => setAddOpen(false)}>Create account</Button>
          </>
        }
      >
        <FormGrid>
          <Field label="Account name" required span={2}>
            <TextInput placeholder="e.g. KKR Real Estate" />
          </Field>
          <Field label="Type" required>
            <Select defaultValue="Investor">
              <option>Investor</option>
              <option>3PM</option>
              <option>JV</option>
              <option>Broker</option>
              <option>Vendor</option>
            </Select>
          </Field>
          <Field label="Status" required>
            <Select defaultValue="Prospect">
              <option>Prospect</option>
              <option>Client</option>
              <option>Investor</option>
              <option>Suspect</option>
            </Select>
          </Field>
          <Field label="Referred by" span={2}>
            <TextInput placeholder="Sourcing relationship" />
          </Field>
        </FormGrid>
      </Modal>
    </>
  )
}

function AccountCard({ card, onOpen }: { card: AccountCardData; onOpen: () => void }) {
  const { account } = card
  const splitParts: string[] = []
  if (card.owned) splitParts.push(`${num(card.owned)} owned`)
  if (card.managed) splitParts.push(`${num(card.managed)} managed`)
  const split = splitParts.join(' · ')

  return (
    <Card className="acct-card" onClick={onOpen}>
      <div className="acct-card-top">
        <Avatar name={account.logoText ?? account.name} size={40} tone={card.prospect ? 'slate' : 'navy'} />
        <div className="acct-card-id">
          <Link
            to={`/accounts/${account.id}`}
            className="acct-card-name"
            onClick={(e) => e.stopPropagation()}
          >
            {account.name}
          </Link>
          <div className="acct-card-badges">
            <Badge tone={TYPE_TONE[card.type] ?? 'neutral'} variant="outline">{card.type}</Badge>
            {card.prospect
              ? <Badge tone="warn" variant="soft">{card.status}</Badge>
              : <HealthBadge health={card.health} />}
          </div>
        </div>
      </div>

      {card.prospect ? (
        <div className="acct-prospect-note">
          {account.sourceReferredBy
            ? <>Sourced by <strong>{account.sourceReferredBy}</strong></>
            : 'Prospect capital — not yet committed.'}
        </div>
      ) : (
        <>
          <div className="acct-stat-row">
            <div className="acct-stat">
              <div className="label">Properties</div>
              <div className="acct-stat-val num">{num(card.propertyCount)}</div>
              {split && <div className="acct-stat-sub dim">{split}</div>}
            </div>
            <div className="acct-stat">
              <div className="label">Portfolio Occ.</div>
              <div className="acct-stat-val num">{pct(card.occupancy)}</div>
              <div className="acct-stat-spark">
                {card.occSeries.length > 2
                  ? <Sparkline data={card.occSeries} width={84} height={22} color={C.cyan} />
                  : <span className="acct-stat-sub dim">—</span>}
              </div>
            </div>
          </div>

          <div className="acct-card-foot">
            <div className="acct-foot-cell">
              <div className="label">In-place revenue / mo</div>
              <div className="acct-foot-val num">{money(card.inPlaceRevenue, { compact: true })}</div>
            </div>
            <div className="acct-foot-meta">
              {card.fundCount > 0 && (
                <span className="acct-foot-chip">{num(card.fundCount)} {card.fundCount === 1 ? 'fund' : 'funds'}</span>
              )}
              <span className={`acct-foot-chip${card.openItems > 0 ? ' acct-foot-chip--warn' : ''}`}>
                {card.openItems > 0 ? `${num(card.openItems)} open` : 'No open items'}
              </span>
            </div>
          </div>
        </>
      )}
    </Card>
  )
}
